const DEFAULT_MAX_ENTRIES = 24;
const DEFAULT_FRESH_MS = 30_000;
const DEFAULT_STALE_READABLE_MS = 5 * 60_000;
const DEFAULT_HARD_EXPIRY_MS = 30 * 60_000;

export class ConversationThreadCache {
  constructor({
    maxEntries = DEFAULT_MAX_ENTRIES,
    freshMs = DEFAULT_FRESH_MS,
    staleReadableMs = DEFAULT_STALE_READABLE_MS,
    hardExpiryMs = DEFAULT_HARD_EXPIRY_MS,
    now = () => Date.now(),
  } = {}) {
    this.maxEntries = maxEntries;
    this.freshMs = freshMs;
    this.staleReadableMs = staleReadableMs;
    this.hardExpiryMs = hardExpiryMs;
    this.now = now;
    this.entries = new Map();
  }

  makeKey({ sessionId, workspaceHint = null, centralInbox = false }) {
    const session = String(sessionId ?? "").trim();
    const workspace = String(workspaceHint ?? "").trim() || "unknown";
    return `${centralInbox ? "central" : "workspace"}:${workspace}:${session}`;
  }

  read(key) {
    const entry = this.entries.get(key);
    if (!entry) return { status: "miss", data: null };

    const ageMs = Math.max(0, this.now() - entry.updatedAt);
    if (ageMs >= this.hardExpiryMs) {
      this.entries.delete(key);
      return { status: "miss", data: null };
    }

    this.entries.delete(key);
    this.entries.set(key, entry);

    if (ageMs < this.freshMs) return { status: "fresh", data: entry.data };
    if (ageMs < this.staleReadableMs) return { status: "stale", data: entry.data };
    return { status: "expired", data: null };
  }

  set(key, data) {
    if (!key || !data) return;
    this.entries.delete(key);
    this.entries.set(key, { data, updatedAt: this.now() });
    while (this.entries.size > this.maxEntries) {
      const oldestKey = this.entries.keys().next().value;
      this.entries.delete(oldestKey);
    }
  }

  updateSession(sessionId, updater) {
    const target = String(sessionId ?? "").trim();
    if (!target || typeof updater !== "function") return;
    const matchingEntries = [...this.entries].filter(
      ([, entry]) => String(entry.data?.id ?? "").trim() === target,
    );
    for (const [key, entry] of matchingEntries) {
      const next = updater(entry.data);
      if (next) this.set(key, next);
    }
  }

  invalidateSession(sessionId, workspaceHint = null) {
    const target = String(sessionId ?? "").trim();
    const workspace = String(workspaceHint ?? "").trim();
    for (const [key, entry] of this.entries) {
      if (String(entry.data?.id ?? "").trim() !== target) continue;
      if (workspace && String(entry.data?.workspaceId ?? "").trim() !== workspace) continue;
      this.entries.delete(key);
    }
  }

  clear() {
    this.entries.clear();
  }
}
